import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { adminGuard } from './guards/adminGuard'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/HomeView.vue'),
    meta: {
      title: 'Главная',
    },
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: {
      title: 'Панель управления',
      requiresAuth: true,
    },
  },
  {
    path: '/about',
    name: 'About',
    component: () => import('@/views/AboutView.vue'),
    meta: {
      title: 'О портале',
    },
  },
  // MF routes (динамически загружаемые микрофронтенды)
  {
    path: '/mf/:mfId',
    name: 'MFView',
    component: () => import('@/views/mf/MFView.vue'),
    meta: {
      title: 'Модуль',
    },
  },

  // Auth routes
  {
    path: '/auth/yandex/callback',
    name: 'YandexCallback',
    component: () => import('@/views/auth/YandexCallbackView.vue'),
    meta: {
      title: 'Авторизация',
    },
  },
  {
    path: '/auth/callback',
    name: 'CrossDomainCallback',
    component: () => import('@/views/auth/CrossDomainCallbackView.vue'),
    meta: {
      title: 'Авторизация',
    },
  },

  // Admin routes
  {
    path: '/admin',
    component: () => import('@/views/admin/AdminLayout.vue'),
    beforeEnter: adminGuard,
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
    },
    children: [
      {
        path: '',
        redirect: '/admin/dashboard',
      },
      {
        path: 'dashboard',
        name: 'AdminDashboard',
        component: () => import('@/views/admin/AdminDashboard.vue'),
        meta: {
          title: 'Dashboard',
        },
      },
      {
        path: 'orgs',
        name: 'AdminOrgs',
        component: () => import('@/views/admin/orgs/OrgsListView.vue'),
        meta: {
          title: 'Организации',
        },
      },
      {
        path: 'orgs/:slug',
        name: 'AdminOrgDetail',
        component: () => import('@/views/admin/orgs/OrgDetailView.vue'),
        meta: {
          title: 'Организация',
        },
      },
      {
        path: 'orgs/:slug/users',
        name: 'AdminOrgUsers',
        component: () => import('@/views/admin/orgs/OrgUsersView.vue'),
        meta: {
          title: 'Пользователи организации',
        },
      },
      // Projects
      {
        path: 'projects',
        name: 'AdminProjects',
        component: () => import('@/views/admin/projects/ProjectsListView.vue'),
        meta: {
          title: 'Проекты',
        },
      },
      {
        path: 'projects/:id',
        name: 'AdminProjectDetail',
        component: () => import('@/views/admin/projects/ProjectDetailView.vue'),
        meta: {
          title: 'Проект',
        },
      },
      {
        path: 'projects/:id/users',
        name: 'AdminProjectUsers',
        component: () => import('@/views/admin/projects/ProjectUsersView.vue'),
        meta: {
          title: 'Участники проекта',
        },
      },
      {
        path: 'consultant/presets',
        name: 'AdminConsultantPresets',
        component: () => import('@/views/admin/consultant/ConsultantPresetsView.vue'),
        meta: {
          title: 'Пресеты консультанта',
        },
      },
      {
        path: 'consultant/knowledge',
        name: 'AdminKnowledgeBase',
        component: () => import('@/views/admin/consultant/KnowledgeBaseView.vue'),
        meta: {
          title: 'Knowledge Base',
        },
      },
      {
        path: 'consultant/knowledge/:fileId/edit',
        name: 'AdminKnowledgeEditor',
        component: () => import('@/views/admin/consultant/KnowledgeEditorView.vue'),
        meta: {
          title: 'Редактирование файла',
        },
      },
      {
        path: 'consultant/rag',
        name: 'AdminConsultantRAG',
        component: () => import('@/views/admin/consultant/RAGGeneratorView.vue'),
        meta: {
          title: 'RAG генерация',
        },
      },
      {
        path: 'modules',
        name: 'AdminModules',
        component: () => import('@/views/admin/modules/ModulesAdminView.vue'),
        meta: {
          title: 'Модули',
        },
      },
      {
        path: 'modules/clock',
        name: 'AdminClockSettings',
        component: () => import('@/views/admin/modules/ClockSettingsView.vue'),
        meta: {
          title: 'Настройки часов',
        },
      },
    ],
  },

  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFoundView.vue'),
    meta: {
      title: 'Страница не найдена',
    },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  },
})

// Обновление заголовка страницы
router.beforeEach((to, _from, next) => {
  const title = to.meta.title as string | undefined
  document.title = title ? `${title} | TLH.ru` : 'TLH.ru'
  next()
})

export default router
